import type { ClassNamesConfig, GroupBase } from 'react-select';
import { cn } from '../tokens/cn';

export interface SelectClassNamesOptions {
  size?: 'sm' | 'md' | 'lg';
}

const heightBySize = {
  sm: '!min-h-8',
  md: '!min-h-10',
  lg: '!min-h-12',
};

const textBySize = {
  sm: '!text-sm',
  md: '!text-sm',
  lg: '!text-base',
};

/**
 * Token-driven `classNames` config for react-select. Pass to the `classNames`
 * prop on a `<Select>` component to apply the design system theme.
 *
 * Use the `unstyled` prop on react-select alongside this to remove its default
 * styles entirely.
 */
export const buildSelectClassNames = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  size = 'md',
}: SelectClassNamesOptions = {}): ClassNamesConfig<Option, IsMulti, Group> => ({
  control: ({ isFocused, isDisabled }) =>
    cn(
      '!bg-[var(--tk-surface-bg-elevated)] !text-[var(--tk-surface-fg)]',
      '!border !rounded-tk-md !shadow-none',
      isFocused
        ? '!border-[var(--tk-brand-bg)] !ring-2 !ring-[var(--tk-brand-ring)]'
        : '!border-[var(--tk-surface-border-strong)]',
      isDisabled && '!opacity-60 !cursor-not-allowed',
      heightBySize[size],
      textBySize[size],
    ),
  valueContainer: () => '!px-3',
  input: () => '!text-[var(--tk-surface-fg)] !m-0 !p-0',
  placeholder: () => '!text-[var(--tk-surface-fg-subtle)]',
  singleValue: () => '!text-[var(--tk-surface-fg)]',
  multiValue: () =>
    cn(
      '!bg-[var(--tk-brand-bg-subtle)] !rounded-tk-sm !mr-1',
    ),
  multiValueLabel: () =>
    '!text-[var(--tk-brand-fg-on-subtle)] !text-xs !font-medium !px-2 !py-0.5',
  multiValueRemove: () =>
    cn(
      '!text-[var(--tk-brand-fg-on-subtle)] !rounded-r-tk-sm',
      'hover:!bg-[var(--tk-brand-bg)] hover:!text-[var(--tk-brand-fg)]',
    ),
  indicatorSeparator: () => '!bg-[var(--tk-surface-border)]',
  dropdownIndicator: () =>
    '!text-[var(--tk-surface-fg-muted)] hover:!text-[var(--tk-surface-fg)] !p-1.5',
  clearIndicator: () =>
    '!text-[var(--tk-surface-fg-muted)] hover:!text-[var(--tk-danger-bg)] !p-1.5',
  loadingIndicator: () => '!text-[var(--tk-brand-bg)] !p-1.5',
  menu: () =>
    cn(
      '!bg-[var(--tk-surface-bg-elevated)] !text-[var(--tk-surface-fg)]',
      '!border !border-[var(--tk-surface-border)] !rounded-tk-md !shadow-tk-popover',
      '!mt-1',
    ),
  menuList: () => '!p-1',
  option: ({ isSelected, isFocused, isDisabled }) =>
    cn(
      '!cursor-pointer !rounded-tk-sm !text-sm !px-2.5 !py-1.5',
      isSelected && '!bg-[var(--tk-brand-bg)] !text-[var(--tk-brand-fg)] !font-medium',
      !isSelected && isFocused && '!bg-[var(--tk-brand-bg-subtle)] !text-[var(--tk-brand-fg-on-subtle)]',
      !isSelected && !isFocused && '!bg-transparent !text-[var(--tk-surface-fg)]',
      isDisabled && '!opacity-50 !cursor-not-allowed',
    ),
  groupHeading: () =>
    '!px-2 !py-1.5 !text-xs !font-semibold !uppercase !tracking-wider !text-[var(--tk-surface-fg-muted)]',
  noOptionsMessage: () =>
    '!text-[var(--tk-surface-fg-muted)] !text-sm !px-2.5 !py-2',
  loadingMessage: () =>
    '!text-[var(--tk-surface-fg-muted)] !text-sm !px-2.5 !py-2',
});
